import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../../lib/routing/frontend';
import { isSeniorStaff } from '../../../lib/helper/authorisation';
import { consultantDisplayName } from '../../../lib/helper/formatting';

export const ManageHospitalConsultants = ({ hospital, updateHospitalConsultantRelation }) => {
  const [updatingRelations, setUpdatingRelations] = useState({});

  const handleDirectPayToggle = async (hospitalConsultantId, currentValue) => {
    if (!isSeniorStaff()) {
      return null;
    }

    setUpdatingRelations((prev) => ({ ...prev, [hospitalConsultantId]: true }));

    try {
      await updateHospitalConsultantRelation(hospitalConsultantId, {
        isDirectPay: !currentValue,
      });
    } catch (error) {
      console.error('Failed to update consultant relation:', error);
    } finally {
      setUpdatingRelations((prev) => ({ ...prev, [hospitalConsultantId]: false }));
    }
  };

  const handleVendorNumberChange = async (hospitalConsultantId, vendorNumber) => {
    if (!isSeniorStaff()) {
      return null;
    }

    setUpdatingRelations((prev) => ({ ...prev, [hospitalConsultantId]: true }));

    try {
      await updateHospitalConsultantRelation(hospitalConsultantId, {
        vendorNumber,
      });
    } catch (error) {
      console.error('Failed to update vendor number:', error);
    } finally {
      setUpdatingRelations((prev) => ({ ...prev, [hospitalConsultantId]: false }));
    }
  };

  if (!hospital?.hospitalConsultants?.length) {
    return (
      <div className="crud__section mb-3 bg-white">
        <div className="crud__section-icon">
          <i className="fas fa-user-md" />
        </div>
        <h2 className="crud__section-header">Linked Consultants</h2>
        <p className="text-muted">No consultants linked to this hospital</p>
      </div>
    );
  }

  return (
    <div className="crud__section mb-3 bg-white">
      <div className="crud__section-icon">
        <i className="fas fa-user-md" />
      </div>
      <h2 className="crud__section-header">Linked Consultants</h2>
      <p className="text-muted mb-3">Manage consultant relationships and payment settings for {hospital.name}</p>

      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Consultant</th>
              <th>Direct Pay</th>
              <th>Vendor Number</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {hospital.hospitalConsultants
              ?.filter((relation) => relation.consultant && relation.consultant.name)
              .map((hospitalConsultant) => {
                const consultant = hospitalConsultant.consultant;
                const isUpdating = updatingRelations[hospitalConsultant.id];
                return (
                  <tr key={hospitalConsultant.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div>
                          <strong>{consultantDisplayName(consultant)}</strong>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {isSeniorStaff() ? (
                          <Form.Check
                            type="switch"
                            id={`directPay-${hospitalConsultant.id}`}
                            checked={hospitalConsultant.isDirectPay || false}
                            onChange={() => handleDirectPayToggle(hospitalConsultant.id, hospitalConsultant.isDirectPay)}
                            disabled={isUpdating}
                            label=""
                          />
                        ) : (
                          <span className={`badge ${hospitalConsultant.isDirectPay ? 'badge-success' : 'badge-secondary'}`}>
                            {hospitalConsultant.isDirectPay ? 'Direct Pay' : 'MediAccount'}
                          </span>
                        )}
                        <span className="ml-2">
                          {isUpdating && <i className="fas fa-spinner fa-spin text-muted" />}
                          {!isUpdating && (
                            <span className={`text-${hospitalConsultant.isDirectPay ? 'success' : 'muted'}`}>
                              {hospitalConsultant.isDirectPay ? 'Direct Pay' : 'MediAccount Payment'}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td>
                      {isSeniorStaff() ? (
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Enter vendor number"
                          defaultValue={hospitalConsultant.vendorNumber || ''}
                          onBlur={(e) => {
                            const newValue = e.target.value.trim();
                            if (newValue !== (hospitalConsultant.vendorNumber || '')) {
                              handleVendorNumberChange(hospitalConsultant.id, newValue || null);
                            }
                          }}
                          disabled={isUpdating}
                          style={{ maxWidth: '200px' }}
                        />
                      ) : (
                        <span className="text-muted">{hospitalConsultant.vendorNumber || '-'}</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link
                        to={ROUTES.CONSULTANTS.SINGLE.replace(':id', consultant.id) + `?tab=clinics`}
                        className="btn btn-sm btn-outline-primary"
                        title={`View ${consultant.name}`}>
                        <i className="fas fa-eye mr-1" />
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
